import { useEffect, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { Modal, ModalFooter, Button } from './ui';
import {
  setSessionExpiredListener,
  resetSessionExpiredSignal,
} from '../services/apiClient';

// Global modal that surfaces expired-session state to the user. Registers a
// singleton listener with apiClient — when any API request 401s or when the
// active provider's silent-auth fails to renew the token, this modal opens
// with a "Sign in" CTA that kicks off the provider-appropriate login flow.
//
// Mounted once near the app root. Guarded so it doesn't fire before the
// user has ever authenticated (e.g. mid-boot silent-auth in flight).
export default function SessionExpiredModal() {
  const { isAuthenticated, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    setSessionExpiredListener(() => {
      // The listener is called from apiClient with no idea whether the user
      // was ever logged in. If they weren't, there's nothing to expire — the
      // regular login flow (ProtectedRoute) handles that case.
      if (!isAuthenticated) return;
      setOpen(true);
    });
    return () => {
      setSessionExpiredListener(null);
      resetSessionExpiredSignal();
    };
  }, [isAuthenticated]);

  const handleSignIn = async () => {
    setSigningIn(true);
    // Clears local provider state — GoogleAdapter re-renders the login
    // gate. Google clears in place, with no redirect.
    await logout();
  };

  return (
    <Modal
      open={open}
      onClose={() => { /* not dismissible — the only path forward is Sign in */ }}
      title="Session expired"
      showCloseButton={false}
      size="sm"
    >
      <p className="text-sm text-neutral-700">
        You've been signed out due to inactivity. Sign in again to continue —
        any unsaved changes in this tab may be lost.
      </p>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={handleSignIn}
          disabled={signingIn}
        >
          {signingIn ? 'Redirecting…' : 'Sign in'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
